import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import { fmtD } from '../utils/format';

function AdjustStockModal({ clients, onClose, onSubmit, submitting }) {
  const [clientId, setClientId] = useState('');
  const [material, setMaterial] = useState('');
  const [boardSize, setBoardSize] = useState('');
  const [gsm, setGsm] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  // Form helper: pre-fill size/gsm if client has existing stock
  const { state } = useApp();
  const clientStock = useMemo(() => {
    if (!clientId) return [];
    const set = new Set();
    const opts = [];
    state.ledger.forEach(item => {
      if (item.client_id === clientId) {
        const key = `${item.material}|${item.size}|${item.gsm}`;
        if (!set.has(key)) {
          set.add(key);
          opts.push({ material: item.material, size: item.size, gsm: item.gsm });
        }
      }
    });
    return opts;
  }, [clientId, state.ledger]);

  return createPortal(
    <div className="modal-overlay active" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 400 }}>
        <div className="modal-header">
          <h3><i className="fa-solid fa-scale-balanced"></i> Manual Stock Adjustment</h3>
          <button className="btn-close-modal" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={e => {
          e.preventDefault();
          onSubmit({
            client_id: clientId,
            material: material,
            board_size: boardSize,
            gsm: Number(gsm),
            quantity_delta: Number(quantity),
            note: note,
          });
        }}>
          <div className="modal-body">
            <div className="form-group">
              <label>Client *</label>
              <select required value={clientId} onChange={e => setClientId(e.target.value)} style={{ width: '100%', padding: '10px', background: 'var(--bg-dark)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                <option value="">— Select Client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Material *</label>
              <input required type="text" placeholder="e.g. CARTE LUMINA" value={material} onChange={e => setMaterial(e.target.value)} className="editable-input" list="material-opts" />
              <datalist id="material-opts">
                {Array.from(new Set(clientStock.map(s => s.material))).filter(Boolean).map((m, i) => <option key={i} value={m} />)}
              </datalist>
            </div>
            
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label>Board Size *</label>
                <input required type="text" placeholder="e.g. 20x30" value={boardSize} onChange={e => setBoardSize(e.target.value)} className="editable-input" list="size-opts" />
                <datalist id="size-opts">
                  {Array.from(new Set(clientStock.map(s => s.size))).map((s, i) => <option key={i} value={s} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>GSM *</label>
                <input required type="number" placeholder="e.g. 250" value={gsm} onChange={e => setGsm(e.target.value)} className="editable-input" list="gsm-opts" />
                <datalist id="gsm-opts">
                  {Array.from(new Set(clientStock.map(s => s.gsm))).map((s, i) => <option key={i} value={s} />)}
                </datalist>
              </div>
            </div>

            <div className="form-group">
              <label>Quantity (Sheets) *</label>
              <input required type="number" placeholder="e.g. 500 (add) or -200 (remove)" value={quantity} onChange={e => setQuantity(e.target.value)} className="editable-input" />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Use negative number to remove stock.</span>
            </div>

            <div className="form-group">
              <label>Reason / Notes *</label>
              <input required type="text" placeholder="e.g. 10 sheets damaged during handling" value={note} onChange={e => setNote(e.target.value)} className="editable-input" />
            </div>
          </div>
          
          <div className="modal-footer" style={{ marginTop: 20 }}>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Applying…' : 'Apply Adjustment'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function StockLedger() {
  const { state, cl, fetchLedger, doAdjustStock } = useApp();
  const [activeTab, setActiveTab] = useState('balances');
  const [ledgerFilterClientId, setLedgerFilterClientId] = useState('');
  
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const isFirstLoad = state.loading.ledger && !state.ledger.length;

  // 1. Compute Client Stock Balances Summary dynamically from ledger movements
  const balancesSummary = useMemo(() => {
    const summaryMap = {};
    state.ledger.forEach(item => {
      const client = cl(item.client_id);
      const key = `${item.client_id}|${(item.material || '').toUpperCase()}|${item.size}|${item.gsm}`;
      if (!summaryMap[key]) {
        summaryMap[key] = {
          client_id: item.client_id,
          client_name: client ? client.name : 'Unknown Client',
          material: item.material,
          board_size: item.size,
          gsm: item.gsm,
          balance: 0
        };
      }
      // qty is already stored as positive (inward) or negative (consumed)
      summaryMap[key].balance += (item.qty || 0);
    });
    return Object.values(summaryMap).filter(b => b.balance > 0);
  }, [state.ledger, cl]);

  // 2. Filter critical low stock items (< 300 sheets)
  const lowStockAlerts = useMemo(() => {
    return balancesSummary.filter(b => b.balance < 300);
  }, [balancesSummary]);

  // 3. Filter Transaction Ledger History
  const filteredLedger = useMemo(() => {
    // Sort descending (newest first)
    const sorted = [...state.ledger].sort((a, b) => b.at.localeCompare(a.at));
    if (ledgerFilterClientId) {
      return sorted.filter(item => item.client_id === ledgerFilterClientId);
    }
    return sorted;
  }, [state.ledger, ledgerFilterClientId]);

  const handleAdjustSubmit = async (data) => {
    setAdjusting(true);
    try {
      await doAdjustStock(data);
      setShowAdjustModal(false);
    } catch (_) {}
    finally { setAdjusting(false); }
  };

  if (isFirstLoad) {
    return <Layout><PageLoader label="Loading stock ledger…" /></Layout>;
  }

  return (
    <Layout
      actions={
        <>
          <Button variant="primary" onClick={() => setShowAdjustModal(true)}>
            <i className="fa-solid fa-scale-balanced"></i> Adjust Stock
          </Button>
          <Button variant="outline" onClick={fetchLedger}>
            ↺ Refresh Ledger
          </Button>
        </>
      }
    >
      <div className="tabs-container">
        {/* Tab Buttons */}
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'balances' ? 'active' : ''}`}
            onClick={() => setActiveTab('balances')}
          >
            Stock Balances
          </button>
          <button
            className={`tab-btn ${activeTab === 'ledger' ? 'active' : ''}`}
            onClick={() => setActiveTab('ledger')}
          >
            Transaction Ledger History
          </button>
        </div>

        {/* Tab Content Panels */}
        <div className="tabs-content">
          
          {/* ── Tab Pane 1: Stock Balances ── */}
          {activeTab === 'balances' && (
            <div className="tab-pane active">
              <div className="inventory-balances-row">
                
                {/* Left Card: Balance Table */}
                <div className="balances-table-card glass-card">
                  <div className="card-header">
                    <h3>Current Stock Levels</h3>
                  </div>
                  <div className="table-wrapper">
                    <table className="dashboard-table">
                      <thead>
                        <tr>
                          <th>Client Name</th>
                          <th>Material Type</th>
                          <th>Board Size</th>
                          <th>GSM</th>
                          <th>Unit of Measure</th>
                          <th className="align-right">Current Balance (Sheets)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {balancesSummary.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="placeholder-text">
                              Inventory is empty. Confirm inward invoices to log stock.
                            </td>
                          </tr>
                        ) : (
                          balancesSummary.map((item, idx) => (
                            <tr key={idx}>
                              <td><strong>{item.client_name}</strong></td>
                              <td>{item.material}</td>
                              <td><code>{item.board_size}</code></td>
                              <td>{item.gsm} GSM</td>
                              <td>Sheets</td>
                              <td className="align-right"><strong>{item.balance.toLocaleString()}</strong></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right Card: Low Stock Alerts */}
                <div className="alerts-side-card glass-card">
                  <div className="card-header">
                    <h3><i className="fa-solid fa-triangle-exclamation"></i> Low Stock Alerts</h3>
                  </div>
                  <div className="alert-list">
                    {lowStockAlerts.length === 0 ? (
                      <p className="placeholder-text">All stock levels healthy.</p>
                    ) : (
                      lowStockAlerts.map((item, idx) => (
                        <div key={idx} className="stock-alert-item">
                          <i className="fa-solid fa-triangle-exclamation"></i>
                          <div className="stock-alert-details">
                            <h4>{item.client_name}</h4>
                            <p>Low inventory level for <strong>{item.material} ({item.board_size} / {item.gsm} GSM)</strong>.</p>
                            <span className="badge badge-danger">{item.balance} sheets left</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Tab Pane 2: Transaction History ── */}
          {activeTab === 'ledger' && (
            <div className="tab-pane active">
              <div className="glass-card">
                <div className="card-header search-row">
                  <h3>Stock Transaction Ledger</h3>
                  <div className="filter-group">
                    <select
                      className="select-dropdown"
                      value={ledgerFilterClientId}
                      onChange={e => setLedgerFilterClientId(e.target.value)}
                      style={{
                        background: 'var(--bg-dark)', color: 'var(--text-primary)', border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)', padding: '8px 14px', fontSize: 13, outline: 'none'
                      }}
                    >
                      <option value="">All Clients</option>
                      {state.clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="table-wrapper">
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th>Material</th>
                        <th>Board Size</th>
                        <th>GSM</th>
                        <th>Transaction Type</th>
                        <th>Quantity Sheets</th>
                        <th>Balance sheets</th>
                        <th>Source Reference</th>
                        <th>Shop floor Inventory</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLedger.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="placeholder-text">No ledger transactions found.</td>
                        </tr>
                      ) : (
                        filteredLedger.map((item, idx) => {
                          const client = cl(item.client_id);
                          const isPositive = item.qty > 0;
                          const isNegative = item.qty < 0;
                          const sign = isPositive ? '+' : '';
                          const rowClass = isPositive ? 'trend positive' : isNegative ? 'trend negative' : 'trend';
                          
                          let badgeType = 'badge-secondary';
                          if (item.type === 'inward') badgeType = 'badge-success';
                          else if (item.type === 'consumed') badgeType = 'badge-danger';
                          else if (item.type === 'adjustment') {
                            badgeType = isPositive ? 'badge-success' : 'badge-danger';
                          }
                          
                          // Format source reference
                          const inwardInvoice = item.inward_id
                            ? state.inward.find(entry => entry.id === item.inward_id)?.inv_no
                            : null;
                          const workcardTicket = item.workcard_id
                            ? state.workcards.find(wc => wc.id === item.workcard_id)?.no
                            : null;

                          const refShort = item._raw?.reference_number || item._raw?.reference_no
                            ? `Ref: ${item._raw?.reference_number || item._raw?.reference_no}`
                            : item.inward_id
                            ? `Invoice: ${inwardInvoice || item.inward_id.substr(0, 8)}`
                            : item.workcard_id
                            ? `Ticket: ${workcardTicket || item.workcard_id.substr(0, 8)}`
                            : 'Adjust';

                          return (
                            <tr key={idx}>
                              <td>{client ? client.name : 'Unknown Client'}</td>
                              <td>{item.material}</td>
                              <td><code>{item.size}</code></td>
                              <td>{item.gsm}</td>
                              <td>
                                <span className={`badge ${badgeType}`} style={{ textTransform: 'capitalize' }}>
                                  {item.type}
                                </span>
                              </td>
                              <td className={rowClass}>
                                <strong>{sign}{item.qty.toLocaleString()}</strong>
                              </td>
                              <td>{(item._raw?.balance_sheets ?? 0).toLocaleString()}</td>
                              <td><code>{refShort}</code></td>
                              <td>{fmtD(item.at)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {showAdjustModal && (
        <AdjustStockModal 
          clients={state.clients.filter(c => c.status !== 'inactive')}
          onClose={() => setShowAdjustModal(false)}
          onSubmit={handleAdjustSubmit}
          submitting={adjusting}
        />
      )}
    </Layout>
  );
}
